'use client';

import React from 'react';
import { RiSparkling2Line } from 'react-icons/ri';
import Image from 'next/image';
import LouiseButton from '@/components/buttons/LouiseButton';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from '@/hooks/useTranslation';
import { BiDollar } from 'react-icons/bi';

type Props = {
  locale: string;
};

const GeneralChatbotBenefits = ({ locale }: Props) => {
  const { t } = useTranslation(locale);

  return (
    <section className="tw-py-10 lg:tw-py-20 tw-font-['Lexend'] tw-bg-transparent">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <RiSparkling2Line className="tw-w-4 tw-h-4" />
            {t('3generalChatbotBenefit/features-badge')}
          </div>
          <h2 className="tw-text-3xl lg:tw-text-4xl xl:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
            {t('3generalChatbotBenefit/title')}
          </h2>
          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto">{t('3generalChatbotBenefit/sub-title')}</p>
        </div>

        <div className="tw-grid grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
          <div className="tw-group tw-bg-gradient-to-br tw-from-white tw-to-gray-50/70 tw-rounded-3xl tw-shadow-lg tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-[1.02] tw-border tw-border-gray-200/60 tw-flex tw-flex-col tw-backdrop-blur-sm">
            <div className="tw-relative tw-overflow-hidden tw-p-1">
              <Image
                priority={true}
                quality={100}
                src="https://cdn.1cdn.app/application/LOUISEBOT/2507101559407_roi.png"
                alt="ROI"
                width={400}
                height={250}
                className="tw-w-full tw-h-48 tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
              />
              <div
                className={`tw-absolute tw-top-8 tw-left-8 tw-w-12 tw-h-12 tw-bg-blue-500 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shadow-lg`}
              >
                <BiDollar className={`tw-w-6 tw-h-6 tw-text-white`} />
              </div>
              {/* Metric Card */}
              <div className="tw-absolute tw-bottom-8 tw-right-8 tw-bg-white/95 tw-backdrop-blur-md tw-rounded-xl tw-px-4 tw-py-3 tw-shadow-lg tw-ring-1 tw-ring-gray-200/50">
                <div className="tw-text-lg tw-font-bold tw-text-gray-900">
                  {t('3generalChatbotBenefit/bloc1/metric')}
                </div>
                <div className="tw-text-xs tw-text-gray-600 tw-font-medium">
                  {t('3generalChatbotBenefit/bloc1/metricLabel')}
                </div>
              </div>
            </div>
            <div className="tw-p-8 tw-flex tw-flex-col tw-flex-grow">
              <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
                {t('3generalChatbotBenefit/bloc1/title')}
              </h3>
              <p className="tw-text-gray-600 tw-mb-6 tw-leading-relaxed tw-flex-grow tw-text-sm">
                {ReactHtmlParser(t('3generalChatbotBenefit/bloc1/description'))}
              </p>
              <LouiseButton
                color="blue"
                cta={t('3generalChatbotBenefit/bloc1/cta')}
                ctaTarget="register"
                gradiant={true}
                locale={locale}
              />
            </div>
          </div>
          <div className="tw-group tw-bg-gradient-to-br tw-from-white tw-to-gray-50/70 tw-rounded-3xl tw-shadow-lg tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-[1.02] tw-border tw-border-gray-200/60 tw-flex tw-flex-col tw-backdrop-blur-sm">
            <div className="tw-relative tw-overflow-hidden tw-p-1">
              <Image
                priority={true}
                quality={100}
                src="https://cdn.1cdn.app/application/LOUISEBOT/25071015594050_white_label.png"
                alt="White_label"
                width={400}
                height={250}
                className="tw-w-full tw-h-48 tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
              />
              <div
                className={`tw-absolute tw-top-8 tw-left-8 tw-w-12 tw-h-12 tw-bg-purple-500 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shadow-lg`}
              >
                <BiDollar className={`tw-w-6 tw-h-6 tw-text-white`} />
              </div>
              {/* Metric Card */}
              <div className="tw-absolute tw-bottom-8 tw-right-8 tw-bg-white/95 tw-backdrop-blur-md tw-rounded-xl tw-px-4 tw-py-3 tw-shadow-lg tw-ring-1 tw-ring-gray-200/50">
                <div className="tw-text-lg tw-font-bold tw-text-gray-900">
                  {t('3generalChatbotBenefit/bloc2/metric')}
                </div>
                <div className="tw-text-xs tw-text-gray-600 tw-font-medium">
                  {t('3generalChatbotBenefit/bloc2/metricLabel')}
                </div>
              </div>
            </div>
            <div className="tw-p-8 tw-flex tw-flex-col tw-flex-grow">
              <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
                {t('3generalChatbotBenefit/bloc2/title')}
              </h3>
              <p className="tw-text-gray-600 tw-mb-6 tw-leading-relaxed tw-flex-grow tw-text-sm">
                {ReactHtmlParser(t('3generalChatbotBenefit/bloc2/description'))}
              </p>
              <LouiseButton
                color="purple"
                cta={t('3generalChatbotBenefit/bloc2/cta')}
                ctaTarget="register"
                gradiant={true}
                locale={locale}
              />
            </div>
          </div>
          <div className="tw-group tw-bg-gradient-to-br tw-from-white tw-to-gray-50/70 tw-rounded-3xl tw-shadow-lg tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-[1.02] tw-border tw-border-gray-200/60 tw-flex tw-flex-col tw-backdrop-blur-sm">
            <div className="tw-relative tw-overflow-hidden tw-p-1">
              <Image
                priority={true}
                quality={100}
                src="https://cdn.1cdn.app/application/LOUISEBOT/25071015593961_delivery.png"
                alt="delivery"
                width={400}
                height={250}
                className="tw-w-full tw-h-48 tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
              />
              <div
                className={`tw-absolute tw-top-8 tw-left-8 tw-w-12 tw-h-12 tw-bg-green-500 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-ring-2 tw-ring-white/50`}
              >
                <BiDollar className={`tw-w-6 tw-h-6 tw-text-white`} />
              </div>
              {/* Metric Card */}
              <div className="tw-absolute tw-bottom-8 tw-right-8 tw-bg-white/95 tw-backdrop-blur-md tw-rounded-xl tw-px-4 tw-py-3 tw-shadow-lg tw-ring-1 tw-ring-gray-200/50">
                <div className="tw-text-lg tw-font-bold tw-text-gray-900">
                  {t('3generalChatbotBenefit/bloc3/metric')}
                </div>
                <div className="tw-text-xs tw-text-gray-600 tw-font-medium">
                  {t('3generalChatbotBenefit/bloc3/metricLabel')}
                </div>
              </div>
            </div>
            <div className="tw-p-8 tw-flex tw-flex-col tw-flex-grow">
              <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
                {t('3generalChatbotBenefit/bloc3/title')}
              </h3>
              <p className="tw-text-gray-600 tw-mb-6 tw-leading-relaxed tw-flex-grow tw-text-sm">
                {ReactHtmlParser(t('3generalChatbotBenefit/bloc3/description'))}
              </p>
              <LouiseButton
                color="green"
                cta={t('3generalChatbotBenefit/bloc3/cta')}
                ctaTarget="register"
                gradiant={true}
                locale={locale}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GeneralChatbotBenefits;
